import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import * as yup from "yup";
import { Button } from '../buttons'
import { Header, DataGrid, DataGridColumn, sortRows, mergeRows, InvalidRowFlag } from '../layout'
import { validate } from '../validation'
import { FormPageContext, FormGridContext, FormGridMehods, FormGridDetailsMehods } from './Types'

// Preconfigured grid properies
export interface FormGridProps<M extends {}, R extends {}> {
  id?: string;
  ctx: FormPageContext<M>
  header: string | JSX.Element
  noRowsMessage: string
  additionalElement?: string | JSX.Element
  addButton: string
  columns: DataGridColumn<R>[]
  schema: yup.ObjectSchema<R>
  rows: R[]
  setRows: (model: M, newRows: R[]) => M
  content: (ctx: FormGridContext<R>) => JSX.Element
  canExpand?:(row: R)=>boolean
	canRemove?:(row: R)=>boolean
}

// Pre configured grid to be reused across form pages
const FormGridInternal = <M extends {}, R extends {}>(props: FormGridProps<M, R>, ref: React.ForwardedRef<FormGridMehods<M, R>>) => {
  const { readOnly, model, onChange } = props.ctx;
  // Ref to expanded section
  const detailsRef = useRef<FormGridDetailsMehods<R>>(null);

  // Configure grid expand/colapse
  const [invalidRows, setInvalidRows] = useState<R[]>([]);

  // Configure grid expand/colapse
  const [expanded, setExpanded] = useState<(R | null)[]>([]);

  // Configure grid sorting
  const [sorting, setSorting] = useState({
    columnIndex: -1,
    descending: true
  });
  const onSort = (columnIndex: number, descending: boolean) => {
    const newRows = sortRows(props.rows, props.columns, columnIndex, descending);
    onChange(props.setRows(model, newRows));
    setSorting({ columnIndex, descending });
  }

  // Revalidating rows when page is displayed
  const [validationCounter, setValidationCounter] = useState(0);
  const revalidate = async () => {
    const newInvalidRows:R[] = [];
    
    let rows: R[]; 
    if(props.canExpand){
      rows = props.rows.filter(r=> props.canExpand!(r));
    }
    else{
      rows = props.rows; 
    }
    var rowValidation = await Promise.all(rows.map(r => {return validate<R>(props.schema, r);}));
    for(const v of rowValidation) {
      const [errors, row] = v;
      if(typeof errors == "object" ? Object.keys(errors).length > 0 : errors) {
        newInvalidRows.push(row);
      }
    }
    setInvalidRows(newInvalidRows);
  }
  useEffect(() => {
    if(validationCounter == 0) {
      // Allow state to be commited
      setTimeout(() => setValidationCounter(validationCounter + 1), 1);
    }
    else {
      revalidate();
    }
  }, [validationCounter])

  // Notify parent page
  useEffect(() => {
    props.ctx.resetErrorsFlag();
  }, [invalidRows, expanded])

  // Remove invalid rows that no longer exist
  useEffect(() => {
    const newInvalidRows:R[] = []
    for(let r of invalidRows) {
      if(props.rows.indexOf(r) >= 0) {
        newInvalidRows.push(r);
      }
    }
    setInvalidRows(newInvalidRows);
  }, [props.rows])

  // Hanlder to commit changes
  const onRowChange = (current: R | null, update: R | null) => {
    const newRows = sortRows(mergeRows(props.rows, current, update), props.columns, sorting.columnIndex, sorting.descending);
    setExpanded(expanded.filter(r => r != current));
    onChange(props.setRows(model, newRows));
  }

  // Implement exposed methods, specifically to return uncommited details
  useImperativeHandle(ref, () => ({
    getUncommited: async (revalidate: boolean, colapse: boolean) => {
      if (detailsRef.current != null) {
        const [current, update, invalid] = await detailsRef.current.getUncommited(revalidate)
        const newRows = sortRows(mergeRows(props.rows, current, update), props.columns, sorting.columnIndex, sorting.descending);
        if (colapse || revalidate && !invalid) {
          setExpanded([]);
        }
        return [newRows, invalid ? invalid : invalidRows.filter(r=> r!= current).length > 0];
      }
      else {
        return [props.rows, invalidRows.length > 0];
      }
    },
    revalidate: ()=>{
      setValidationCounter(validationCounter + 1);
    },
    setRows: (model: M, newRows: R[]) => {
      return props.setRows(model, newRows);
    }
  }));

  const removeItem=(e: React.KeyboardEvent<HTMLAnchorElement> | React.MouseEvent<HTMLAnchorElement>, r: R)=>{
    e.preventDefault();
    if (confirm("Remove?")) {
      const newRows = mergeRows(props.rows, r, null);
      onChange(props.setRows(model, newRows));
      revalidate();
    }
  }

  const columns = props.columns.concat({
    footer: "",
    label: "",
    rightAlign: true,
    width: "10",
    sortable: false,
    value: (r) =>
      (!readOnly && (props.canRemove === undefined || props.canRemove(r))) && (
        <>
          {invalidRows.indexOf(r) >= 0 ? <InvalidRowFlag /> : <></>}
          {" "}
          <a
            href="#"
            onKeyDown={e => {
              if(e.key === 'Enter'){
                removeItem(e, r);
              }
            }}
            onClick={(e) => removeItem(e, r)}
          >
            Remove
          </a>
        </>
      ),
  } as DataGridColumn<R>);
  return <>
    <div className="row mt-4">
      <div className="col">
        <Header label={props.header}></Header>
      </div>
    </div>
    <div className="row mt-2">
      <div className='col'></div>
      {props.additionalElement && (
          <div className='col-auto p-0'>{props.additionalElement}</div>
        )}
      <div className='col-auto'> {!readOnly && (
          <>
            <Button
              id={props.id ? `add-new-item-${props.id}`: 'add-new-item'}
              label={props.addButton}
              disabled={false}
              onClick={() => setExpanded([null])}
            />
          </>
        )}
      </div>
    </div>
    <DataGrid<R>
      rows={props.rows}
      columns={columns}
      noRowsMessage="There are no rows..."
      sort={{
        columnIndex: sorting.columnIndex,
        descending: sorting.descending,
        onChange: onSort
      }}
      expand={{
        rows: expanded,
        canExpand: props.canExpand,
        onChange: setExpanded,
        content: current => props.content({ 
          row: current, onChange
          : onRowChange, 
          readOnly: readOnly, 
          detailsRef, 
          schema: props.schema,
          invalid: current ? invalidRows.indexOf(current) >= 0 : false
        })
      }}
    />
  </>
};

const FormGridWithRef = (forwardRef(FormGridInternal) as any) as <M extends {}, R extends {}>(
  props: FormGridProps<M, R> & { ref: React.ForwardedRef<FormGridMehods<M, R>> }
) => ReturnType<typeof FormGridInternal>;

var _counter = 0;

export const FormGrid = <M extends {}, R extends {}>(props: FormGridProps<M, R>) => {
  const [id] = useState(++_counter);
  return <FormGridWithRef {...props} ref={ref => {
    if(ref != null) {
      props.ctx.gridRefs.current[id] = ref as any;
    }
    else {
      delete props.ctx.gridRefs.current[id];
    }
  }}/>
}